import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
// import { Stack, Typography, Select, MenuItem } from "@mui/material";
// import StyleIcon from "@mui/icons-material/Style";
import { useDCS } from "./DCSContext"; // Import the custom hook
import RowEditableTable from "../../../../../../components/Tables/RowEditableTable";
import UserDialog from "./UsersDialog";
import ForgeViewerModal from "./ViewerModal";
import TimesheetDialog from "../../../../../../components/Dialogs/TimesheetDialog";
import CommentsDialog from "../../../../../../components/Dialogs/CommentsDialog";
import RaciDashboard from "./Dashboard";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import NewRowModal from "./NewRaciModal";
import DeleteConfirmModal from "./DeleteConfirmModal";
import {
  AuthContext,
  useAuthContext,
} from "../../../../../../context/authContext";

// import { RACIColumns } from "./utils";
import RACITable from "./RaciColumns";
import DocumentTransmittalTab from "../DocumentTransmittalTab";
import OldDocumentTransmittalTab from "../DocumentTransmittalTab/OldIndexForModal";
import EmailDialog from "./EmailDialog";

const extractRaciParticipants = (raciItem) => {
  const participants = [];
  const emailSet = new Set();

  const addEmail = (email) => {
    if (email && typeof email === "string" && !emailSet.has(email)) {
      emailSet.add(email);
      participants.push(email);
    }
  };

  const extractEmail = (user) => {
    if (!user) return null;
    if (typeof user === "string") return user;
    if (typeof user === "object" && user.email) return user.email;
    return null;
  };

  const responsibleEmail = extractEmail(raciItem.responsible);
  if (responsibleEmail) addEmail(responsibleEmail);

  const accountable = raciItem.accountable || raciItem.accountables || [];
  const accountableArray = Array.isArray(accountable) ? accountable : [accountable];
  accountableArray.forEach((item) => {
    const email = extractEmail(item);
    if (email) addEmail(email);
  });

  const consulted = raciItem.consulted || [];
  const consultedArray = Array.isArray(consulted) ? consulted : [consulted];
  consultedArray.forEach((item) => {
    const email = extractEmail(item);
    if (email) addEmail(email);
  });

  const informed = raciItem.informed || [];
  const informedArray = Array.isArray(informed) ? informed : [informed];
  informedArray.forEach((item) => {
    const email = extractEmail(item);
    if (email) addEmail(email);
  });

  return participants;
};

export default function RACI({ type, plant = false, projectFilter = "all" }) {
  const { user } = useAuthContext();
  console.log("user", user);

  const currentUser = user;
  const {
    racis,
    allUsers,
    sharepointDocuments,
    urnLoaded,
    addRaci,
    updateRaciData,
    getTimesheetsData,
    createTimesheetData,
    updateTimesheetData,
    deleteTimesheetData,
    getCommentsData,
    createCommentData,
    documentList,
    archiveRaciData,
    currentDocsReviewer,
    plantProjects,
    selectedDocuments,
    setSelectedDocuments,
    updateRaciUsers,
  } = useDCS(); // Use the DCSContext

  // Dialog state management
  const [dialogState, setDialogState] = useState({
    mail: false,
    userDialog: false,
    timesheet: false,
    preview: false,
    comments: false,
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedOption, setSelectedOption] = useState("2D");
  const [newRowModalState, setNewRowModalState] = useState(false);
  const [modalType, setModalType] = useState("acc");
  const [archiveModalState, setArchiveModalState] = useState({
    open: false,
    raciItem: null,
  });

  const handleButtonClick = (type, row) => {
    setSelectedItem(row.original);
    if (type === "acc" || type === "cons" || type === "inf") {
      setModalType(type);
      setDialogState((prev) => ({ ...prev, userDialog: true }));
    } else {
      setDialogState((prev) => ({ ...prev, [type]: true }));
    }
  };

  const handleDialogClose = () => {
    setDialogState({
      mail: false,
      userDialog: false,
      timesheet: false,
      preview: false,
      comments: false,
    });
  };
  const handleSaveRow = async (values) => {
    const newValues = {
      document: values.document,
      docLink: values.docLink,
      revNum: values.revNum,
      responsible: values.responsible,
      type: type,
      docNum: values.docNum,
      // Ensure plant mode passes projectId so addRaci can call the API
      projectId: values.projectId,
      // Not part of the documented RACI DTO - sent optimistically in case
      // the backend already accepts/stores it; if not, most NestJS
      // ValidationPipe configs just strip unknown fields silently.
      ...(values.wbsCode ? { wbsCode: values.wbsCode } : {}),
    };
    return addRaci(newValues);
  };

  const handleCreateRow = () => {
    addRaci({
      document: "",
      revNum: "",
      docLink: "",
      responsible: "",
      type: type,
      isNew: true,
    });
  };

  const handleArchiveClick = (raciItem) => {
    setArchiveModalState({
      open: true,
      raciItem,
    });
  };

  const handleArchiveConfirm = async () => {
    if (archiveModalState.raciItem?.id) {
      const raciId = archiveModalState.raciItem.id;
      setArchiveModalState({ open: false, raciItem: null });

      try {
        await archiveRaciData(raciId);
      } catch (error) {
        console.error("Error archiving RACI:", error);
      }
    }
  };

  const handleArchiveCancel = () => {
    setArchiveModalState({ open: false, raciItem: null });
  };

  const filteredDocList = useMemo(() => {
    if (!type || type === "all") return documentList || [];
    return (documentList || []).filter((doc) => doc.type === type);
  }, [documentList, type]);

  const filteredRacis = useMemo(() => {
    // For plant mode, always show all data (no filtering)
    if (plant) {
      return racis;
    }
    // For non-plant mode, apply project filter if needed
    if (!projectFilter || projectFilter === "all") {
      return racis;
    }
    const normalizedFilter = String(projectFilter);
    return (racis || []).filter((item) => {
      const idMatch =
        item?.projectId !== undefined &&
        String(item.projectId) === normalizedFilter;
      const nameMatch =
        item?.projectName &&
        item.projectName.toLowerCase() === normalizedFilter.toLowerCase();
      return idMatch || nameMatch;
    });
  }, [racis, plant, projectFilter]);

  return (
    <Box
      sx={{ width: "100%", typography: "body1", overflow: "auto", padding: 2 }}
    >
      <RaciDashboard type={type} />
      {/* <RowEditableTable
        columns={RACIColumns({
          allUsers,
          handleButtonClick: handleButtonClick,
          // currentUser,
          sharepointDocuments,
          urnLoaded,
        })}
        data={racis}
        handleCreateRow={() => {
          setNewRowModalState(true);
        }}
        // handleSaveRow={handleSaveRow}
        additionalProps={{ positionGlobalFilter: "left" }}
      /> */}

      {currentDocsReviewer === "doc-transmittal" ? (
        // <OldDocumentTransmittalTab />
        <DocumentTransmittalTab />
      ) : (
        <>
          <RACITable
            data={filteredRacis}
            handleButtonClick={handleButtonClick}
            allUsers={allUsers}
            currentUser={currentUser}
            plant={plant}
            updateRaciData={updateRaciData}
            setSelectedDocuments={setSelectedDocuments}
            openNewRaciModal={() => {
              setNewRowModalState(true);
            }}
            onArchiveRaci={handleArchiveClick}
          />
          <EmailDialog
            open={dialogState.mail}
            handleClose={handleDialogClose}
            row={selectedItem}
          />
          <UserDialog
            open={dialogState.userDialog}
            handleClose={handleDialogClose}
            rowId={selectedItem?.id}
            allUsers={allUsers}
            type={modalType}
            onUsersUpdated={updateRaciUsers}
          />
          <CommentsDialog
            open={dialogState.comments}
            handleClose={handleDialogClose}
            id={selectedItem?.id}
            api={{
              getComments: getCommentsData,
              createComment: createCommentData,
            }}
            parentId={selectedItem?.id}
            parentType={"RACI"}
            participants={selectedItem ? extractRaciParticipants(selectedItem) : []}
            raciId={selectedItem?.id}
          />
          <TimesheetDialog
            open={dialogState.timesheet}
            handleClose={handleDialogClose}
            parentId={selectedItem?.id}
            parentName={selectedItem?.document}
            api={{
              getItems: getTimesheetsData,
              createItem: createTimesheetData,
              updateItem: updateTimesheetData,
              deleteItem: deleteTimesheetData,
            }}
          />
          <ForgeViewerModal
            open={dialogState.preview}
            onClose={handleDialogClose}
            urn={selectedItem?.urn}
          />
          <NewRowModal
            open={newRowModalState}
            handleClose={() => {
              setNewRowModalState(false);
            }}
            allUsers={allUsers}
            docList={filteredDocList}
            handleAddRow={handleSaveRow}
            type={type}
            plant={plant}
            plantProjects={plantProjects}
          />
          <DeleteConfirmModal
            variant="archive"
            open={archiveModalState.open}
            onClose={handleArchiveCancel}
            onConfirm={handleArchiveConfirm}
            itemName={archiveModalState.raciItem?.document || "this item"}
          />
        </>
      )}
    </Box>
  );
}
